
import { Button } from "@/components/ui/button";
import { Store, Bell, Sparkles, BarChart, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";

export function HeroSection() {
  return (
    <section 
      className="relative my-8 md:my-12 overflow-hidden border border-metal-light/30 rounded-2xl shadow-xl"
      itemScope 
      itemType="https://schema.org/WebPageElement"
    >
      {/* Background effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-gold-light/20 via-background/80 to-silver-light/20 backdrop-blur-sm"></div>
      
      <div className="absolute inset-0 opacity-10 z-0" 
           style={{ 
             backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.15'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
             backgroundSize: '30px 30px'
           }}>
      </div>
      
      {/* Decorative glows */}
      <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-gold-light/30 blur-3xl" aria-hidden="true"></div>
      <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-silver-light/30 blur-3xl" aria-hidden="true"></div>
      
      {/* Floating decorative icons */}
      <motion.div 
        className="absolute top-1/4 right-[10%] opacity-40 rotate-12"
        animate={{ y: [0, -10, 0], opacity: [0.4, 0.7, 0.4] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        aria-hidden="true"
      >
        <Sparkles className="w-16 h-16 text-gold drop-shadow-lg" />
      </motion.div>
      
      <motion.div 
        className="absolute bottom-1/4 left-[10%] opacity-40 -rotate-12"
        animate={{ y: [0, 10, 0], opacity: [0.4, 0.7, 0.4] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        aria-hidden="true"
      >
        <BarChart className="w-16 h-16 text-silver drop-shadow-lg" />
      </motion.div>
      
      {/* Main content */}
      <div className="relative z-10 py-16 md:py-24 px-6 md:px-12 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-center md:text-left"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              <Badge 
                variant="gold" 
                className="px-4 py-1.5 text-sm mb-6 shadow-md"
                itemProp="specialty"
              >
                <span className="animate-pulse mr-2 h-2 w-2 rounded-full bg-gold" aria-hidden="true"></span>
                Monitoraggio Prezzi in Tempo Reale
              </Badge>
            </motion.div>
            
            {/* Titles */}
            <motion.h1 
              className="text-5xl md:text-6xl font-extrabold mb-6 leading-tight tracking-tight"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.7 }}
              itemProp="headline"
            >
              <span className="block text-foreground drop-shadow-sm mb-2">
                Prezzi <span itemProp="about">Oro</span> e <span itemProp="about">Argento</span>
              </span>
              <span className="bg-gradient-to-r from-gold via-gold-dark to-silver-dark bg-clip-text text-transparent drop-shadow-sm">
                in Tempo Reale
              </span>
            </motion.h1>
            
            {/* Description */}
            <motion.p 
              className="text-xl mb-10 max-w-2xl mx-auto md:mx-0 text-foreground/90 leading-relaxed font-medium"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.7 }}
              itemProp="description"
            >
              Monitora i <strong>prezzi metalli preziosi</strong>, trova i migliori <strong>compro oro verificati</strong> e ottieni <strong>valutazioni istantanee</strong> per i tuoi preziosi.
            </motion.p>
            
            {/* CTA Buttons */}
            <motion.div 
              className="flex flex-wrap gap-4 justify-center md:justify-start"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.7 }}
              role="group"
              aria-label="Azioni principali"
            >
              <motion.div 
                whileHover={{ scale: 1.05, boxShadow: "0 10px 25px -5px rgba(187, 145, 26, 0.5)" }}
                whileTap={{ scale: 0.98 }}
                className="shadow-lg hover:shadow-gold/30 transition-all duration-300 rounded-lg w-full md:w-auto"
              >
                <Link to="/compro-oro" className="block w-full" itemProp="potentialAction" itemScope itemType="https://schema.org/SearchAction">
                  <Button 
                    size="lg" 
                    className="bg-gradient-to-r from-gold to-gold-light hover:from-gold-light hover:to-gold text-white font-semibold px-6 py-6 h-auto w-full"
                    aria-describedby="find-dealer-description"
                  >
                    <span>Trova il Miglior Compro Oro</span>
                    <Store className="ml-2 opacity-80 group-hover:translate-x-1 transition-transform duration-300" aria-hidden="true" />
                  </Button>
                  <span id="find-dealer-description" className="sr-only">
                    Cerca e confronta i migliori compro oro nella tua zona
                  </span>
                </Link>
              </motion.div>
              
              <motion.div 
                whileHover={{ scale: 1.05, boxShadow: "0 10px 25px -5px rgba(187, 145, 26, 0.2)" }}
                whileTap={{ scale: 0.98 }}
                className="shadow-md hover:shadow-metal/20 transition-all duration-300 rounded-lg w-full md:w-auto"
              >
                <Link to="/avvisi" className="block w-full" itemProp="potentialAction" itemScope itemType="https://schema.org/CreateAction">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="border-metal text-foreground font-semibold backdrop-blur-sm bg-background/50 px-6 py-6 h-auto w-full"
                    aria-describedby="price-alerts-description"
                  >
                    <span>Imposta Avvisi</span>
                    <Bell className="ml-2 opacity-80 group-hover:translate-y-[-2px] transition-transform duration-300" aria-hidden="true" />
                  </Button>
                  <span id="price-alerts-description" className="sr-only">
                    Crea notifiche personalizzate per variazioni di prezzo
                  </span>
                </Link>
              </motion.div>
            </motion.div>
            
            {/* Extra Link */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.1, duration: 0.7 }}
              className="mt-8 text-center md:text-left"
            >
              <Link 
                to="/grafici" 
                className="inline-flex items-center text-gold hover:text-gold-dark transition-colors group"
                aria-describedby="charts-link-description"
              >
                <span>Visualizza grafici dettagliati</span>
                <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
              </Link>
              <span id="charts-link-description" className="sr-only">
                Accedi ai grafici interattivi con storico prezzi metalli preziosi
              </span>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
